import { Link } from 'react-router-dom';
import { HELP_CENTER_URL } from '@/lib/config/external-links';
import { WaypointMark } from '../brand/WaypointMark';
import { openCookiePreferences } from './CookieBanner';

const PRODUCT_LINKS = [
  { label: 'Waypoint Owner', to: '/platform/owner' },
  { label: 'Waypoint Sales', to: '/platform/sales' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Help Center', href: HELP_CENTER_URL, external: true },
] as const;

const RESOURCE_LINKS = [
  { label: 'Roadmap', to: '/roadmap' },
  { label: 'Status', to: '/status' },
  { label: 'Contact', href: 'mailto:support@waypoint-intel.com' },
] as const;

const LEGAL_LINKS = [
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Privacy Policy', to: '/privacy' },
  // Withdrawing consent must be as easy as giving it (GDPR Art. 7(3)) — this
  // reopens the consent banner so an earlier choice can be changed.
  { label: 'Cookie Preferences', onClick: openCookiePreferences },
] as const;

const SOCIAL_LINKS = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/waypoint-intel/',
    icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/waypointintel/',
    // Rounded square, lens ring and flash dot, cut with evenodd so it sits
    // next to the filled LinkedIn mark at the same weight.
    icon: 'M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm5.5-3.75a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z',
  },
] as const;

function FooterLinkGroup({
  title,
  links,
}: {
  title: string;
  links: ReadonlyArray<{
    label: string;
    href?: string;
    to?: string;
    external?: boolean;
    onClick?: () => void;
  }>;
}) {
  return (
    <div>
      <h3 className="mb-3 font-mono text-[10px] font-medium uppercase tracking-widest text-ink-dim">
        {title}
      </h3>
      <ul className="flex flex-col gap-2.5">
        {links.map((link) => (
          <li key={link.label}>
            {link.onClick ? (
              <button
                type="button"
                onClick={link.onClick}
                className="text-left text-[13px] text-ink-muted transition-colors hover:text-ink"
              >
                {link.label}
              </button>
            ) : link.to ? (
              <Link
                to={link.to}
                className="text-[13px] text-ink-muted transition-colors hover:text-ink"
              >
                {link.label}
              </Link>
            ) : (
              <a
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                className="text-[13px] text-ink-muted transition-colors hover:text-ink"
              >
                {link.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MarketingFooter() {
  return (
    <footer className="border-t border-border bg-surface-0">
      <div className="mx-auto max-w-6xl px-6 py-12 md:px-12 md:py-16">
        {/* 4-column grid */}
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
          {/* Column 1: Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="mb-4 inline-flex items-center gap-2">
              <WaypointMark size={28} />
              <span className="text-[13px] font-semibold tracking-tight text-ink">
                Waypoint
              </span>
            </Link>
            <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-ink-muted">
              Leverage intelligence to engineer outcomes. Predictive models turn your data into predictions, actions, and revenue.
            </p>
            {/* Social links */}
            <div className="mt-5 flex items-center gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-ink-dim transition-colors hover:border-primary-line hover:text-primary-bright"
                  aria-label={`Waypoint on ${social.label}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d={social.icon} />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Product */}
          <FooterLinkGroup title="Product" links={PRODUCT_LINKS} />

          {/* Column 3: Resources */}
          <FooterLinkGroup title="Resources" links={RESOURCE_LINKS} />

          {/* Column 4: Legal */}
          <FooterLinkGroup title="Legal" links={LEGAL_LINKS} />
        </div>

        {/* Bottom bar */}
        <div className="mt-12 border-t border-border pt-6">
          <p className="text-[12px] text-ink-dim">
            &copy; {new Date().getFullYear()} Waypoint Intelligence. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
